import { Popover } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { useEffect, useRef, useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import { BsCalendar4Week } from "react-icons/bs";
import CustomStyledTable from "../../../../components/table/CustomStyledTable";
import { APP_CONFIG } from "../../../../helpers/appHelpers";
import { useManageTableWithSearchParams } from "../../../../helpers/useManageTable";
import { useGetOrganization } from "../../../../services/organization.service";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import { TransactionsColumns } from "../data";
import TransactionModal from "../modals/TransactionModal";
import {
	useGetOrgTransaction,
	useGetOrgTransactionSearch,
} from "../../../../services/organization-transaction.service";
import {
	OrganizationTransaction,
	TRANSACTION_TYPE,
} from "../../../../models/organization-transaction.dto";
import TableFilter from "../../../../components/table/filter/TableFilter";
import { useGetInvoice } from "../../../../services/invoice.service";
import APISearchInput from "../../../../components/search/APISearchInput";
import { OrganizationOrderBy } from "../../../../models/organization.dto";
import { transactionsColumns } from "../tanstack-columns";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { useTableConfiguration } from "../../../../helpers/useTableConfiguration";
import { TableId } from "../../../../models/table-configuration.dto";
import useUserStore from "../../../../state-management/useUserStore";

type FormAction = "CREATE" | "REVERSE";

const TransactionsView = () => {
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openModal, setOpenModal] = useState(false);
	const [formAction, setFormAction] = useState<FormAction>("CREATE");
	const { formDataValues, setFormData } = useEditFormStore();
	const currentUser = useUserStore().currentUser.user;

	const { mutate: searchTransactions } = useGetOrgTransactionSearch();
	const {
		tableAPIRef,
		fetchedTableData,
		tableData,
		isLoading,
		pageCount,
		setTableData,
	} = useManageTableWithSearchParams<OrganizationTransaction>({
		fetchHook: useGetOrgTransaction,
	});

	// Modal Form select fields API data source
	const options = {
		limit: APP_CONFIG.PAGE_LIMIT,
		page: 1,
		orderBy: "createdAt:desc" as any,
	};
	const { data: invoices } = useGetInvoice(options);
	const { data: organizations } = useGetOrganization({
		...options,
		orderBy: OrganizationOrderBy.NAME_ASC,
	});

	const handleClose = () => setAnchorEl(null);
	const handleOpenModal = () => {
		setAnchorEl(null);
		setFormAction("CREATE");
		setOpenModal(true);
	};

	const additionalColumns = [
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			minWidth: 150,
			flex: 1,
			headerClassName: "super-app-theme--header",
			cellClassName: "actions ",
			getActions: (params: any) => {
				return [
					<GridActionsCellItem
						icon={<BiDotsHorizontalRounded />}
						label="actions"
						onClick={(e: any) => {
							// console.log(params.row, "params");
							setFormData(params?.row);
							setAnchorEl(e.currentTarget);
						}}
					/>,
				];
			},
		},
	];

	const columnsWithAdditions = TransactionsColumns.map(
		(obj) => additionalColumns.find((o) => o.field === obj.field) || obj
	);

	return (
		<>
			<div className="flex justify-between mb-6">
				<div className="flex items-center gap-3">
					<APISearchInput
						fallbackData={fetchedTableData}
						setTableData={setTableData}
						mutate={searchTransactions}
					/>
					<TableFilter
						fields={[
							{
								title: "",
								fieldName: "",
								fieldType: "SORT",
								options: [
									{
										title: "Date Created",
										fieldName: "createdAt",
									},
								],
							},
							{
								title: "Organization",
								fieldName: "organizationId",
								fieldType: "SELECT_FIELD",
								options: organizations?.data || [],
							},
							{
								title: "Type",
								fieldName: "type",
								fieldType: "CHECKBOX_LIST",
								options: TRANSACTION_TYPE,
							},
							{
								title: "Invoice",
								fieldName: "invoiceId",
								fieldType: "SELECT_FIELD",
								options: invoices?.data || [],
								mapText: "referenceCode",
							},
							{
								title: "Date Created",
								fieldName: "createdAt",
								fieldType: "DATE_RANGE",
							},
						]}
					/>
				</div>
				<div
					className="flex items-center text-primary-blue cursor-pointer"
					onClick={handleOpenModal}
				>
					<AiOutlinePlus className="mr-1 text-base" />
					<span className="text-sm">New Transaction</span>
				</div>
			</div>
			<TanStackEmployeeTable
				tableId={TableId.TRANSACTIONS}
				data={tableData}
				columns={transactionsColumns}
				loading={isLoading}
				onRowClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				onActionClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={{}}
				pageSize={10}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			{/* <CustomStyledTable
				rows={tableData || []}
				columns={columnsWithAdditions}
				apiRef={tableAPIRef}
				loading={isLoading}
				pageCount={pageCount}
				setAnchorEl={setAnchorEl}
			/> */}
			<Popover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				PaperProps={{
					style: {
						width: "auto",
						borderRadius: 8,
						display: "flex",
						justifyContent: "center",
					},
				}}
			>
				<div className="flex flex-col justify-center p-3 text-secondary-darkgray">
					<div
						onClick={() => {
							if (formDataValues?.type === "CREDIT") {
								setFormAction("REVERSE");
								setOpenModal(true);
								handleClose();
							}
						}}
						className={`table-action-popover ${
							formDataValues?.type !== "CREDIT" ? "opacity-40" : ""
						}`}
						style={{
							cursor:
								formDataValues?.type !== "CREDIT" ? "not-allowed" : "pointer",
						}}
					>
						<BsCalendar4Week />
						<p className="text-2sm">Reverse</p>
					</div>
				</div>
			</Popover>

			{openModal && (
				<TransactionModal
					open={openModal}
					setOpen={setOpenModal}
					formAction={formAction}
					organizations={organizations?.data || []}
				/>
			)}
		</>
	);
};

export default TransactionsView;
